import validationMessages from "../validationMessages";
import {
    MAX_COMPANY_NAME_LENGTH,
    MAX_JOB_TITLE_LENGTH,
    SERVER_MAX_USER_NAME_LENGTH,
    SERVER_MAX_EMAIL_LENGTH,
    SERVER_MAX_PASSWORD_LENGTH,
    PASSWORD_MIN_LENGTH
} from "../constants";
import {
    ParsedRegisterAccountErrors,
    RegisterAccountServerErrorResponse,
    RegistrationErrorMessage,
    RegistrationFormType
} from "../types";

const FORM_LEVEL = "__form__" as const;
type Target = keyof RegistrationFormType | typeof FORM_LEVEL;

const genericError: RegistrationErrorMessage = { descriptor: validationMessages.genericError };

function buildErrorMap(
    hasDeveloperSandbox: boolean
): Record<string, { target: Target; message: RegistrationErrorMessage }> {
    const accountNameField: keyof RegistrationFormType = hasDeveloperSandbox ? "companyName" : "accountName";
    const accountNameInvalid: RegistrationErrorMessage = {
        descriptor: hasDeveloperSandbox
            ? validationMessages.companyNameInvalidChars
            : validationMessages.accountNameInvalid
    };
    const accountNameTooLong: RegistrationErrorMessage = {
        descriptor: validationMessages.companyNameTooLong,
        values: { maxCompanyNameLength: MAX_COMPANY_NAME_LENGTH }
    };
    const passwordRules: RegistrationErrorMessage = {
        descriptor: validationMessages.passwordRules,
        values: { minLength: PASSWORD_MIN_LENGTH }
    };

    return {
        "createAccount.validator.account.accountName.isNotValid": { target: accountNameField, message: accountNameInvalid },
        "createAccount.validator.account.accountName.isTooLong": { target: accountNameField, message: accountNameTooLong },
        "createAccount.validator.user.firstName.isRequired": { target: "accountName", message: { descriptor: validationMessages.nameRequired } },
        "createAccount.validator.user.firstName.isTooLong": { target: "accountName", message: { descriptor: validationMessages.serverNameTooLong, values: { maxLength: SERVER_MAX_USER_NAME_LENGTH } } },
        "createAccount.validator.user.lastName.isRequired": { target: "accountName", message: { descriptor: validationMessages.fullNameRequired } },
        "createAccount.validator.user.lastName.isTooLong": { target: "accountName", message: { descriptor: validationMessages.serverNameTooLong, values: { maxLength: SERVER_MAX_USER_NAME_LENGTH } } },
        "createAccount.validator.user.email.isRequired": { target: "email", message: { descriptor: validationMessages.emailRequired } },
        "createAccount.validator.user.email.isTooLong": { target: "email", message: { descriptor: validationMessages.emailTooLong, values: { maxLength: SERVER_MAX_EMAIL_LENGTH } } },
        "createAccount.validator.user.email.isNotValid": { target: "email", message: { descriptor: validationMessages.emailWrong } },
        "createAccount.validator.user.email.isNotUnique": { target: "email", message: { descriptor: validationMessages.emailRegistered } },
        "createAccount.validator.user.email.isInBlockList": { target: "email", message: { descriptor: validationMessages.emailDomainInBlockedList, richType: "blockedDomain" } },
        "createAccount.validator.user.password.isRequired": { target: "password", message: { descriptor: validationMessages.passwordRequired } },
        "createAccount.validator.user.password.isNotValid.tooLong": { target: "password", message: { descriptor: validationMessages.passwordTooLong, values: { maxLength: SERVER_MAX_PASSWORD_LENGTH } } },
        "createAccount.validator.user.password.isNotValid.tooShort": { target: "password", message: passwordRules },
        "createAccount.validator.user.password.isNotValid.passwordPatternString": { target: "password", message: passwordRules },
        "createAccount.validator.messages.user.password.isNotValid.otherPasswordError": { target: "password", message: { descriptor: validationMessages.passwordOtherError } },
        "createAccount.validator.user.password.confirm.isNotValid": { target: "confirmPassword", message: { descriptor: validationMessages.passwordDoNotMatch } },
        "createAccount.validator.user.jobTitle.isNotValid": { target: "jobTitle", message: { descriptor: validationMessages.jobTitleInvalidChars } },
        "createAccount.validator.user.jobTitle.isTooLong": { target: "jobTitle", message: { descriptor: validationMessages.jobTitleTooLong, values: { maxJobTitleLength: MAX_JOB_TITLE_LENGTH } } },
        "createAccount.validator.recaptcha.token.isRequired": { target: FORM_LEVEL, message: { descriptor: validationMessages.recaptchaFailed } },
        "createAccount.validator.recaptcha.token.isNotValid": { target: FORM_LEVEL, message: { descriptor: validationMessages.recaptchaFailed } },
        "createAccount.validator.protectedAccountRegistrationPassword.isNotValid": { target: FORM_LEVEL, message: genericError },
        "resource.not.found": { target: FORM_LEVEL, message: { descriptor: validationMessages.invalidProductUid } },
        "createAccount.validator.signup.blocked": { target: FORM_LEVEL, message: { descriptor: validationMessages.signupBlocked } }
    };
}

export function parseRegisterAccountErrors(
    error: RegisterAccountServerErrorResponse,
    hasDeveloperSandbox: boolean
): ParsedRegisterAccountErrors {
    const serverErrors = error.data.response?.errors ?? [];
    const map = buildErrorMap(hasDeveloperSandbox);
    const result: ParsedRegisterAccountErrors = { fieldErrors: {} };
    let sawUnmapped = false;

    serverErrors.forEach(({ key }) => {
        const entry = map[key];

        if (!entry) {
            console.error("Unmapped registration error key", key);
            sawUnmapped = true;

            return;
        }

        if (entry.target === FORM_LEVEL) {
            result.formError = result.formError ?? entry.message; // first wins
        } else if (!result.fieldErrors[entry.target]) {
            result.fieldErrors[entry.target] = entry.message; // first wins
        }
    });

    if (!result.formError && sawUnmapped) {
        result.formError = genericError;
    }

    return result;
}
